import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  ChevronDown,
  ChevronRight,
  FileText,
  Folder,
  HardDrive,
  Link as LinkIcon,
  Loader2,
  RefreshCw,
  Trash2,
  Upload,
} from 'lucide-react';
import { authAPI, projectsAPI, repositoryAPI } from '../../services/api';
import toast from '../../services/toast';
import { useFeatureFlag } from '../../contexts/FeatureFlagContext';
import GoogleDriveBrowseModal from '../../components/googleDrive/GoogleDriveBrowseModal';

function buildTree(sources) {
  const byId = new Map();
  const childrenByParent = new Map();
  for (const s of sources || []) {
    byId.set(s.id, s);
    const pid = s.parentSourceId || null;
    const arr = childrenByParent.get(pid) || [];
    arr.push(s);
    childrenByParent.set(pid, arr);
  }
  return { byId, childrenByParent };
}

function sortFolderFirst(a, b) {
  const af = Boolean(a.isContainer);
  const bf = Boolean(b.isContainer);
  if (af !== bf) return af ? -1 : 1;
  const ap = a.sourcePath || '';
  const bp = b.sourcePath || '';
  if (ap && bp && ap !== bp) return ap.localeCompare(bp, undefined, { sensitivity: 'base' });
  return String(a.title || '').localeCompare(String(b.title || ''), undefined, {
    sensitivity: 'base',
  });
}

/**
 * Uploads, URLs, and processing status for project (or repository-scoped) knowledge.
 * @param {{
 *   repositoryKnowledgeApi?: boolean,
 *   repositoryEntityId?: string,
 *   orgId?: string,
 *   streamId?: string,
 *   projectId?: string,
 *   listRepositoryId?: string|null,
 *   knowRead: boolean,
 *   knowWrite: boolean,
 *   title?: string,
 *   description?: string,
 * }} props
 */
export default function ProjectDocumentsPanel({
  repositoryKnowledgeApi = false,
  repositoryEntityId,
  orgId,
  streamId,
  projectId,
  listRepositoryId = null,
  knowRead,
  knowWrite,
  title = 'Documents',
  description = 'Add roadmaps, ADRs, Confluence exports, security reviews, or other prose. Text is chunked and embedded for portfolio briefings and for Vanti when you chat from this project or repository. Use Browse Google Drive or paste a Drive URL; folder imports materialize the tree (Docs, Sheets, etc.) with server-side limits.',
}: any) {
  const idSuffix = useId().replace(/:/g, '');
  const driveBrowseUi = useFeatureFlag('ui.projectDetail.googleDriveBrowse', false);
  const driveBrowseApi = useFeatureFlag('api.auth.googleDrive.browse', false);
  const driveImportApi = useFeatureFlag('api.organisations.googleDrive.import', false);
  const driveReadApi = useFeatureFlag('api.auth.googleDrive.read', false);
  const showDriveBrowse =
    driveBrowseUi &&
    driveBrowseApi &&
    driveImportApi &&
    driveReadApi &&
    knowWrite &&
    !repositoryKnowledgeApi &&
    orgId &&
    streamId &&
    projectId;
  const [sources, setSources] = useState<any[]>([]);
  const [urlInput, setUrlInput] = useState('');
  const [urlTitle, setUrlTitle] = useState('');
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(() => new Set());
  const [driveConnected, setDriveConnected] = useState(false);
  const [driveBrowseOpen, setDriveBrowseOpen] = useState(false);
  const drivePollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const { childrenByParent } = useMemo(() => buildTree(sources), [sources]);

  const rootItems = useMemo(() => {
    const arr = childrenByParent.get(null) || [];
    return arr.slice().sort(sortFolderFirst);
  }, [childrenByParent]);

  const toggleExpanded = useCallback((id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const loadSources = useCallback(async () => {
    if (!knowRead) return;
    if (repositoryKnowledgeApi && repositoryEntityId) {
      const list = await repositoryAPI.listKnowledgeSources(repositoryEntityId);
      setSources(Array.isArray(list) ? list : []);
      return;
    }
    if (orgId && streamId && projectId) {
      const list = await projectsAPI.listKnowledgeSources(orgId, streamId, projectId, {
        repositoryId: listRepositoryId,
      });
      setSources(Array.isArray(list) ? list : []);
    }
  }, [
    knowRead,
    repositoryKnowledgeApi,
    repositoryEntityId,
    orgId,
    streamId,
    projectId,
    listRepositoryId,
  ]);

  useEffect(() => {
    if (!knowRead) {
      setLoading(false);
      return;
    }
    setLoading(true);
    loadSources()
      .catch((e) => toast.error((e as Error).message || 'Failed to load documents'))
      .finally(() => setLoading(false));
  }, [knowRead, loadSources]);

  useEffect(() => {
    if (!showDriveBrowse) {
      setDriveConnected(false);
      return;
    }
    authAPI
      .getGoogleDriveStatus()
      .then((data) => setDriveConnected(data?.connection?.connected === true))
      .catch(() => setDriveConnected(false));
  }, [showDriveBrowse]);

  const anyProcessing = sources.some(
    (s) => s.processingStatus === 'pending' || s.processingStatus === 'processing'
  );
  useEffect(() => {
    if (!knowRead || !anyProcessing) return;
    const t = setInterval(() => {
      loadSources().catch(() => undefined); // background poll
    }, 4000);
    return () => clearInterval(t);
  }, [anyProcessing, knowRead, loadSources]);

  useEffect(() => {
    return () => {
      if (drivePollRef.current) clearInterval(drivePollRef.current);
    };
  }, []);

  const handleAddUrl = async () => {
    if (!knowWrite || !urlInput.trim()) return;
    try {
      if (repositoryKnowledgeApi && repositoryEntityId) {
        await repositoryAPI.createKnowledgeUrl(repositoryEntityId, {
          url: urlInput.trim(),
          title: urlTitle.trim() || undefined,
        });
      } else if (orgId && streamId && projectId) {
        await projectsAPI.createKnowledgeUrl(orgId, streamId, projectId, {
          url: urlInput.trim(),
          title: urlTitle.trim() || undefined,
          repositoryId: listRepositoryId || undefined,
        });
      }
      setUrlInput('');
      setUrlTitle('');
      await loadSources();
      toast.success('URL source added');
    } catch (e) {
      toast.error((e as Error).message || 'Failed to add URL');
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!knowWrite || !file) return;
    try {
      if (repositoryKnowledgeApi && repositoryEntityId) {
        await repositoryAPI.uploadKnowledgeFile(repositoryEntityId, file);
      } else if (orgId && streamId && projectId) {
        await projectsAPI.uploadKnowledgeFile(orgId, streamId, projectId, file, {
          repositoryId: listRepositoryId || undefined,
        });
      }
      await loadSources();
      toast.success('File uploaded');
    } catch (err) {
      toast.error((err as Error).message || 'Upload failed');
    }
  };

  const handleDriveImportStarted = useCallback(() => {
    void loadSources();
    if (drivePollRef.current) clearInterval(drivePollRef.current);
    const endAt = Date.now() + 3 * 60 * 1000;
    drivePollRef.current = setInterval(() => {
      void loadSources();
      if (Date.now() >= endAt && drivePollRef.current) {
        clearInterval(drivePollRef.current);
        drivePollRef.current = null;
      }
    }, 5000);
  }, [loadSources]);

  const handleReprocess = async (id) => {
    if (!knowWrite) return;
    try {
      if (repositoryKnowledgeApi && repositoryEntityId) {
        await repositoryAPI.reprocessKnowledgeSource(repositoryEntityId, id);
      } else if (orgId && streamId && projectId) {
        await projectsAPI.reprocessKnowledgeSource(orgId, streamId, projectId, id);
      }
      await loadSources();
      toast.success('Reprocess queued');
    } catch (e) {
      toast.error((e as Error).message || 'Reprocess failed');
    }
  };

  const handleDeleteSource = async (id) => {
    if (!knowWrite) return;
    try {
      if (repositoryKnowledgeApi && repositoryEntityId) {
        await repositoryAPI.deleteKnowledgeSource(repositoryEntityId, id);
      } else if (orgId && streamId && projectId) {
        await projectsAPI.deleteKnowledgeSource(orgId, streamId, projectId, id);
      }
      await loadSources();
      toast.success('Source removed');
    } catch (e) {
      toast.error((e as Error).message || 'Delete failed');
    }
  };

  if (!knowRead) {
    return (
      <div className="card-mdl-static p-6 text-sm text-slate-400">
        Document knowledge is disabled for your organisation.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-12 text-slate-400">
        <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
        <span>Loading documents…</span>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-dark-border bg-dark-surface p-5">
      <h3 className="mb-3 text-base font-semibold text-slate-100 inline-flex items-center gap-2">
        <LinkIcon className="h-4 w-4" aria-hidden />
        {title}
      </h3>
      <p className="mb-3 text-xs text-slate-400">{description}</p>
      {knowWrite ? (
        <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-end">
          <div className="min-w-0 flex-1">
            <label className="text-xs font-medium text-slate-400" htmlFor={`kw-url-${idSuffix}`}>
              URL
            </label>
            <input
              id={`kw-url-${idSuffix}`}
              className="mt-1 w-full rounded-md border border-dark-border bg-dark-base px-2 py-1.5 text-sm"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              placeholder="https://…"
            />
          </div>
          <div className="min-w-0 flex-1">
            <label className="text-xs font-medium text-slate-400" htmlFor={`kw-title-${idSuffix}`}>
              Title (optional)
            </label>
            <input
              id={`kw-title-${idSuffix}`}
              className="mt-1 w-full rounded-md border border-dark-border bg-dark-base px-2 py-1.5 text-sm"
              value={urlTitle}
              onChange={(e) => setUrlTitle(e.target.value)}
            />
          </div>
          <button type="button" className="btn-secondary-mdl text-sm" onClick={handleAddUrl}>
            Add URL
          </button>
          {showDriveBrowse ? (
            driveConnected ? (
              <button
                type="button"
                className="btn-secondary-mdl inline-flex items-center gap-2 text-sm"
                onClick={() => setDriveBrowseOpen(true)}
              >
                <HardDrive className="h-4 w-4" aria-hidden />
                Browse Google Drive
              </button>
            ) : (
              <button
                type="button"
                className="btn-secondary-mdl inline-flex items-center gap-2 text-sm"
                onClick={() => setDriveBrowseOpen(true)}
              >
                <HardDrive className="h-4 w-4" aria-hidden />
                Connect Google Drive
              </button>
            )
          ) : null}
          <label className="btn-secondary-mdl inline-flex cursor-pointer items-center gap-2 text-sm">
            <Upload className="h-4 w-4" aria-hidden />
            Upload file
            <input
              type="file"
              className="sr-only"
              accept=".txt,.md,.html,.htm,.json,text/*"
              onChange={handleUpload}
            />
          </label>
        </div>
      ) : null}
      {sources.length === 0 ? (
        <p className="text-sm text-slate-400">No documents yet.</p>
      ) : (
        <ul className="rounded-md border border-dark-border text-sm">
          {rootItems.map((s) => (
            <TreeRow
              key={s.id}
              node={s}
              depth={0}
              childrenByParent={childrenByParent}
              expanded={expanded}
              onToggleExpanded={toggleExpanded}
              knowWrite={knowWrite}
              onReprocess={handleReprocess}
              onDelete={handleDeleteSource}
            />
          ))}
        </ul>
      )}
      {showDriveBrowse ? (
        <GoogleDriveBrowseModal
          isOpen={driveBrowseOpen}
          onClose={() => setDriveBrowseOpen(false)}
          orgId={orgId}
          streamId={streamId}
          projectId={projectId}
          repositoryId={listRepositoryId}
          onImportStarted={handleDriveImportStarted}
          onImported={loadSources}
        />
      ) : null}
    </div>
  );
}

function TreeRow({
  node,
  depth,
  childrenByParent,
  expanded,
  onToggleExpanded,
  knowWrite,
  onReprocess,
  onDelete,
}: any) {
  const kids = (childrenByParent.get(node.id) || []).slice().sort(sortFolderFirst);
  const isFolder = Boolean(node.isContainer);
  const isOpen = isFolder && expanded.has(node.id);
  const pad = depth * 16;

  return (
    <>
      <li className="border-b border-dark-border last:border-b-0">
        <div className="flex flex-col gap-2 px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0" style={{ paddingLeft: pad }}>
            <div className="flex min-w-0 items-center gap-2">
              {isFolder ? (
                <button
                  type="button"
                  className="shrink-0 rounded p-0.5 text-slate-400 hover:bg-dark-base/50"
                  onClick={() => onToggleExpanded(node.id)}
                  aria-label={isOpen ? 'Collapse folder' : 'Expand folder'}
                >
                  {isOpen ? (
                    <ChevronDown className="h-4 w-4" aria-hidden />
                  ) : (
                    <ChevronRight className="h-4 w-4" aria-hidden />
                  )}
                </button>
              ) : (
                <span className="w-5" aria-hidden />
              )}
              {isFolder ? (
                <Folder className="h-4 w-4 shrink-0 text-slate-300" aria-hidden />
              ) : (
                <FileText className="h-4 w-4 shrink-0 text-slate-400" aria-hidden />
              )}
              <div className="min-w-0">
                <div className="truncate font-medium text-slate-100">
                  {node.title}
                  {isFolder ? (
                    <span className="ml-2 text-xs text-slate-400">({kids.length})</span>
                  ) : null}
                </div>
                <div className="truncate text-xs text-slate-400">
                  {node.processingStatus}
                  {node.repositoryId ? ' · repo-scoped' : ''}
                  {node.processingError ? ` · ${node.processingError}` : ''}
                </div>
              </div>
            </div>
          </div>

          {knowWrite ? (
            <div className="flex shrink-0 gap-2">
              <button
                type="button"
                className="btn-icon-mdl text-xs"
                onClick={() => onReprocess(node.id)}
                title="Reprocess"
              >
                <RefreshCw className="h-4 w-4" aria-hidden />
              </button>
              <button
                type="button"
                className="btn-icon-mdl text-xs text-red-600"
                onClick={() => onDelete(node.id)}
                title="Delete"
              >
                <Trash2 className="h-4 w-4" aria-hidden />
              </button>
            </div>
          ) : null}
        </div>
      </li>
      {isOpen
        ? kids.map((k) => (
            <TreeRow
              key={k.id}
              node={k}
              depth={depth + 1}
              childrenByParent={childrenByParent}
              expanded={expanded}
              onToggleExpanded={onToggleExpanded}
              knowWrite={knowWrite}
              onReprocess={onReprocess}
              onDelete={onDelete}
            />
          ))
        : null}
    </>
  );
}
